import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from 'lib/utils';

type WarningAlertProps = {
  children: ReactNode;
  title?: ReactNode;
  className?: string;
};

/**
 * Reusable warning callout with a TriangleAlert icon.
 *
 * @example
 * <WarningAlert>
 *   Changing the embedding model may reprocess existing data.
 * </WarningAlert>
 * <WarningAlert title="View only.">
 *   You are not the owner of this folder.
 * </WarningAlert>
 */
export function WarningAlert({ children, title, className }: WarningAlertProps) {
  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-3 rounded-lg border border-warning/15 bg-warning-background px-3.5 py-3 dark:border-warning/25',
        className
      )}
    >
      <TriangleAlert
        className="mt-0.5 h-4 w-4 shrink-0 text-warning-high-emphasis"
        aria-hidden
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        {title ? (
          <p className="text-xs font-medium leading-5 text-warning-high-emphasis">{title}</p>
        ) : null}
        <div className="text-xs leading-5 text-warning-high-emphasis/90">{children}</div>
      </div>
    </div>
  );
}

export type { WarningAlertProps };
